import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, ArrowLeft, Link as LinkIcon, Trash2, Camera } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import RichTextEditor from '../components/RichTextEditor';
import PhotoGallery from '../components/PhotoGallery';
import VideoGallery, { Video, VideoGalleryHandle } from '../components/VideoGallery';
import DocumentGallery from '../components/DocumentGallery';
import ConfirmDialog from '../components/ConfirmDialog';
import SortableList from '../components/SortableList';
import AddItemsModal from '../components/AddItemsModal';
import VideoUploadModal from '../components/VideoUploadModal';
import VideoPlayer from '../components/VideoPlayer';
import AmenityCard from '../components/AmenityCard';
import AmenityFormModal, { AmenityFormData } from '../components/AmenityFormModal';
import AmenityViewModal from '../components/AmenityViewModal';
import S3Image from '../components/S3Image';
import { Project, ProjectAddress } from '../types/project';
import { Amenity } from '../types/amenity';
import { getImageUrl } from '../services/imageService';
import { getAmenities, createAmenity, updateAmenity, deleteAmenity } from '../services/amenityService';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/ProjectDetails.css';
import '../styles/Buttons.css';

type TabType = 'general' | 'features' | 'amenities' | 'documents' | 'media';

const ProjectDetails: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId, projectId } = useParams<{ workspaceId: string; projectId: string }>();

  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingProjectLogo, setUploadingProjectLogo] = useState(false);
  const [uploadingPortalLogo, setUploadingPortalLogo] = useState(false);
  const [uploadingConstructionLogo, setUploadingConstructionLogo] = useState(false);
  const projectLogoInputRef = useRef<HTMLInputElement>(null);
  const portalLogoInputRef = useRef<HTMLInputElement>(null);
  const constructionLogoInputRef = useRef<HTMLInputElement>(null);

  // Basic Info editing
  const [editingBasicInfo, setEditingBasicInfo] = useState(false);
  const [basicInfoData, setBasicInfoData] = useState({
    name: '',
    projectCode: '',
    ongoingSelectionPhase: '',
    builderVendorNo: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactWebsite: '',
    status: '',
    constructionStartDate: '',
    estimatedCompletionDate: ''
  });
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);

  // Project Address editing
  const [editingProjectAddress, setEditingProjectAddress] = useState(false);
  const [projectAddressData, setProjectAddressData] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    notes: ''
  });
  const [savingProjectAddress, setSavingProjectAddress] = useState(false);

  // Office Address editing
  const [editingOfficeAddress, setEditingOfficeAddress] = useState(false);
  const [officeAddressData, setOfficeAddressData] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    notes: ''
  });
  const [savingOfficeAddress, setSavingOfficeAddress] = useState(false);

  // Description editing
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionData, setDescriptionData] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);

  // Intro Video
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Unit/Lot Information editing
  const [editingUnitInfo, setEditingUnitInfo] = useState(false);
  const [unitInfoData, setUnitInfoData] = useState<any[]>([]);
  const [savingUnitInfo, setSavingUnitInfo] = useState(false);

  // Spaces editing
  const [editingSpaces, setEditingSpaces] = useState(false);
  const [spacesData, setSpacesData] = useState<string[]>([]);
  const [savingSpaces, setSavingSpaces] = useState(false);
  const [showAddSpacesModal, setShowAddSpacesModal] = useState(false);

  // Levels editing
  const [editingLevels, setEditingLevels] = useState(false);
  const [levelsData, setLevelsData] = useState<string[]>([]);
  const [savingLevels, setSavingLevels] = useState(false);
  const [showAddLevelsModal, setShowAddLevelsModal] = useState(false);

  // Views editing
  const [editingViews, setEditingViews] = useState(false);
  const [viewsData, setViewsData] = useState<string[]>([]);
  const [savingViews, setSavingViews] = useState(false);
  const [showAddViewsModal, setShowAddViewsModal] = useState(false);

  // Features description editing
  const [editingFeatures, setEditingFeatures] = useState(false);
  const [featuresData, setFeaturesData] = useState('');
  const [savingFeatures, setSavingFeatures] = useState(false);

  // Amenities
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const [amenityFormOpen, setAmenityFormOpen] = useState(false);
  const [amenityViewOpen, setAmenityViewOpen] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);

  // Videos
  const [videos, setVideos] = useState<Video[]>([]);
  const videoGalleryRef = React.useRef<VideoGalleryHandle>(null);

  // Delete project
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Confirm dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const tabs = [
    { id: 'general' as TabType, label: 'General' },
    { id: 'features' as TabType, label: 'Features and Finishes' },
    { id: 'amenities' as TabType, label: 'Amenities' },
    { id: 'documents' as TabType, label: 'Documents' },
    { id: 'media' as TabType, label: 'Media' }
  ];

  useEffect(() => {
    if (workspaceId && projectId) {
      fetchProject();
      fetchVideos();
    }
  }, [workspaceId, projectId]);

  useEffect(() => {
    if (activeTab === 'amenities') {
      fetchAmenities();
    }
  }, [activeTab]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/workspaces/${workspaceId}/projects/${projectId}`);
      const projectData: Project = response.data.project;
      setProject(projectData);

      // Fetch logo URL if logoId exists
      if (projectData.logoId && workspaceId) {
        try {
          const url = await getImageUrl(workspaceId, projectData.logoId);
          setLogoUrl(url);
        } catch (err) {
          console.error('Error fetching logo URL:', err);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch project details');
      console.error('Error fetching project:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/workspaces/${workspaceId}/projects`);
  };

  const fetchVideos = async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/project/${projectId}/videos`);
      const backendVideos = response.data.videos || [];
      const mappedVideos: Video[] = backendVideos.map((v: any) => ({
        _id: v._id,
        title: v.title || 'Untitled Video',
        url: v.url,
        type: v.source as 'youtube' | 'vimeo' | 'mp4' | 'other',
        thumbnail: v.thumbnailUrl,
        createdAt: v.createdAt
      }));
      setVideos(mappedVideos);
    } catch (err: any) {
      console.error('Error fetching videos:', err);
    }
  };

  const fetchAmenities = async () => {
    try {
      setLoadingAmenities(true);
      const data = await getAmenities(workspaceId!, projectId!);
      setAmenities(data);
    } catch (err: any) {
      console.error('Error fetching amenities:', err);
      toast.error('Failed to load amenities');
    } finally {
      setLoadingAmenities(false);
    }
  };

  const handleAddVideo = async (video: Omit<Video, '_id' | 'createdAt'>) => {
    try {
      const response = await api.post(`/workspaces/${workspaceId}/project/${projectId}/videos`, video);
      const backendVideo = response.data.video;
      const mappedVideo: Video = {
        _id: backendVideo._id,
        title: backendVideo.title || 'Untitled Video',
        url: backendVideo.url,
        type: backendVideo.source as 'youtube' | 'vimeo' | 'mp4' | 'other',
        thumbnail: backendVideo.thumbnailUrl,
        createdAt: backendVideo.createdAt
      };
      setVideos(prev => [...prev, mappedVideo]);
    } catch (err: any) {
      console.error('Error adding video:', err);
      throw err;
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/videos/${videoId}`);
      setVideos(prev => prev.filter(v => v._id !== videoId));
    } catch (err: any) {
      console.error('Error deleting video:', err);
      throw err;
    }
  };

  const handleUpdateVideo = async (videoId: string, title: string) => {
    try {
      await api.put(`/workspaces/${workspaceId}/videos/${videoId}`, { title });
      setVideos(prev => prev.map(v =>
        v._id === videoId ? { ...v, title } : v
      ));
    } catch (err: any) {
      console.error('Error updating video:', err);
      throw err;
    }
  };

  // Generic logo upload handler
  const handleLogoUpload = async (
    file: File,
    logoFieldName: 'logoId' | 'portalHomeLogoId' | 'constructionLogoId',
    logoType: string,
    imageType: 'logo' | 'portal_home_logo' | 'construction_logo',
    setUploadingState: (uploading: boolean) => void
  ) => {
    if (!file || !workspaceId || !projectId) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingState(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'project');
      formData.append('entityId', projectId);
      formData.append('imageType', imageType);

      const uploadResponse = await api.post(
        `/workspaces/${workspaceId}/upload/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const imageId = uploadResponse.data.image._id;

      await api.put(`/workspaces/${workspaceId}/projects/${projectId}`, {
        [logoFieldName]: imageId,
      });

      await fetchProject();
      toast.success(`${logoType} updated successfully`);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to upload ${logoType.toLowerCase()}`);
      console.error(`Error uploading ${logoType}:`, err);
      toast.error(`Failed to upload ${logoType.toLowerCase()}`);
    } finally {
      setUploadingState(false);
    }
  };

  // Project Logo handlers
  const handleProjectLogoClick = () => {
    projectLogoInputRef.current?.click();
  };

  const handleProjectLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleLogoUpload(file, 'logoId', 'Project Logo', 'logo', setUploadingProjectLogo);
      if (projectLogoInputRef.current) {
        projectLogoInputRef.current.value = '';
      }
    }
  };

  // Portal Home Logo handlers
  const handlePortalLogoClick = () => {
    portalLogoInputRef.current?.click();
  };

  const handlePortalLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleLogoUpload(file, 'portalHomeLogoId', 'Purchaser Portal Logo', 'portal_home_logo', setUploadingPortalLogo);
      if (portalLogoInputRef.current) {
        portalLogoInputRef.current.value = '';
      }
    }
  };

  // Construction Company Logo handlers
  const handleConstructionLogoClick = () => {
    constructionLogoInputRef.current?.click();
  };

  const handleConstructionLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleLogoUpload(file, 'constructionLogoId', 'Construction Company Logo', 'construction_logo', setUploadingConstructionLogo);
      if (constructionLogoInputRef.current) {
        constructionLogoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteProject = async () => {
    if (!workspaceId || !projectId) return;

    try {
      setDeleting(true);
      await api.delete(`/workspaces/${workspaceId}/projects/${projectId}`);
      toast.success('Project deleted successfully');
      navigate(`/workspaces/${workspaceId}/projects`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete project');
      console.error('Error deleting project:', err);
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Basic Info handlers
  const handleEditBasicInfo = () => {
    if (!project) return;
    setBasicInfoData({
      name: project.name || '',
      projectCode: project.projectCode || '',
      ongoingSelectionPhase: project.ongoingSelectionPhase || '',
      builderVendorNo: project.builderVendorNo || '',
      contactName: project.contactName || '',
      contactEmail: project.contactEmail || '',
      contactPhone: project.contactPhone || '',
      contactWebsite: project.contactWebsite || '',
      status: project.status || '',
      constructionStartDate: project.constructionStartDate
        ? project.constructionStartDate.split('T')[0]
        : '',
      estimatedCompletionDate: project.estimatedCompletionDate
        ? project.estimatedCompletionDate.split('T')[0]
        : ''
    });
    setEditingBasicInfo(true);
  };

  const handleCancelBasicInfo = () => {
    setEditingBasicInfo(false);
  };

  const handleBasicInfoChange = (field: string, value: string) => {
    setBasicInfoData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveBasicInfo = async () => {
    if (!project) return;
    try {
      setSavingBasicInfo(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        {
          name: basicInfoData.name.trim(),
          projectCode: basicInfoData.projectCode.trim() || undefined,
          ongoingSelectionPhase: basicInfoData.ongoingSelectionPhase.trim() || undefined,
          builderVendorNo: basicInfoData.builderVendorNo.trim() || undefined,
          contactName: basicInfoData.contactName.trim() || undefined,
          contactEmail: basicInfoData.contactEmail.trim() || undefined,
          contactPhone: basicInfoData.contactPhone.trim() || undefined,
          contactWebsite: basicInfoData.contactWebsite.trim() || undefined,
          status: basicInfoData.status.trim() || undefined,
          constructionStartDate: basicInfoData.constructionStartDate || undefined,
          estimatedCompletionDate: basicInfoData.estimatedCompletionDate || undefined
        }
      );
      setProject(response.data.project);
      setEditingBasicInfo(false);
      toast.success('Basic information updated successfully');
    } catch (err: any) {
      console.error('Error updating basic info:', err);
      toast.error(err.response?.data?.message || 'Failed to update basic information');
    } finally {
      setSavingBasicInfo(false);
    }
  };

  // Project Address handlers
  const handleEditProjectAddress = () => {
    if (!project) return;
    setProjectAddressData({
      street1: project.projectAddress?.street1 || '',
      street2: project.projectAddress?.street2 || '',
      city: project.projectAddress?.city || '',
      state: project.projectAddress?.state || '',
      zip: project.projectAddress?.zip || '',
      country: project.projectAddress?.country || 'Canada',
      notes: project.projectAddress?.notes || ''
    });
    setEditingProjectAddress(true);
  };

  const handleCancelProjectAddress = () => {
    setEditingProjectAddress(false);
  };

  const handleProjectAddressChange = (field: string, value: string) => {
    setProjectAddressData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProjectAddress = async () => {
    if (!project) return;
    try {
      setSavingProjectAddress(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { projectAddress: projectAddressData }
      );
      setProject(response.data.project);
      setEditingProjectAddress(false);
      toast.success('Project address updated successfully');
    } catch (err: any) {
      console.error('Error updating project address:', err);
      toast.error(err.response?.data?.message || 'Failed to update project address');
    } finally {
      setSavingProjectAddress(false);
    }
  };

  // Office Address handlers
  const handleEditOfficeAddress = () => {
    if (!project) return;
    setOfficeAddressData({
      street1: project.officeAddress?.street1 || '',
      street2: project.officeAddress?.street2 || '',
      city: project.officeAddress?.city || '',
      state: project.officeAddress?.state || '',
      zip: project.officeAddress?.zip || '',
      country: project.officeAddress?.country || 'Canada',
      notes: project.officeAddress?.notes || ''
    });
    setEditingOfficeAddress(true);
  };

  const handleCancelOfficeAddress = () => {
    setEditingOfficeAddress(false);
  };

  const handleOfficeAddressChange = (field: string, value: string) => {
    setOfficeAddressData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveOfficeAddress = async () => {
    if (!project) return;
    try {
      setSavingOfficeAddress(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { officeAddress: officeAddressData }
      );
      setProject(response.data.project);
      setEditingOfficeAddress(false);
      toast.success('Office address updated successfully');
    } catch (err: any) {
      console.error('Error updating office address:', err);
      toast.error(err.response?.data?.message || 'Failed to update office address');
    } finally {
      setSavingOfficeAddress(false);
    }
  };

  // Description handlers
  const handleEditDescription = () => {
    if (!project) return;
    setDescriptionData(project.description || '');
    setEditingDescription(true);
  };

  const handleCancelDescription = () => {
    setEditingDescription(false);
  };

  const handleSaveDescription = async () => {
    if (!project) return;
    try {
      setSavingDescription(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { description: descriptionData || undefined }
      );
      setProject(response.data.project);
      setEditingDescription(false);
      toast.success('Description updated successfully');
    } catch (err: any) {
      console.error('Error updating description:', err);
      toast.error(err.response?.data?.message || 'Failed to update description');
    } finally {
      setSavingDescription(false);
    }
  };

  // Intro Video handlers
  const handleVideoUploaded = async (videoUrl: string, videoType: 'youtube' | 'vimeo' | 'mp4') => {
    if (!project) return;
    try {
      setUploadingVideo(true);
      console.log('Updating intro video:', { videoUrl, videoType });
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        {
          introVideoUrl: videoUrl,
          introVideoType: videoType
        }
      );
      console.log('Video update response:', response.data);
      setProject(response.data.project);
      toast.success('Intro video added successfully');
    } catch (err: any) {
      console.error('Error updating intro video:', err);
      console.error('Error details:', err.response?.data);
      toast.error(err.response?.data?.message || 'Failed to update intro video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleRemoveVideo = async () => {
    if (!project) return;
    try {
      setUploadingVideo(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        {
          introVideoUrl: null,
          introVideoType: null
        }
      );
      setProject(response.data.project);
      toast.success('Intro video removed successfully');
    } catch (err: any) {
      console.error('Error removing intro video:', err);
      toast.error(err.response?.data?.message || 'Failed to remove intro video');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Unit Info handlers
  const handleEditUnitInfo = () => {
    if (!project) return;
    // If no additionalInfo exists, initialize with all property types
    if (!project.additionalInfo || project.additionalInfo.length === 0) {
      setUnitInfoData([
        { type: 'condominium', totalUnits: undefined, totalFloors: undefined },
        { type: 'detached', totalUnits: undefined },
        { type: 'semi-detached', totalUnits: undefined, totalHomes: undefined },
        { type: 'townhouse', totalBuildings: undefined, totalHomes: undefined }
      ]);
    } else {
      // Ensure all types exist, merge with existing data
      const types: ('condominium' | 'detached' | 'semi-detached' | 'townhouse')[] = ['condominium', 'detached', 'semi-detached', 'townhouse'];
      const existingTypes = project.additionalInfo.map(info => info.type);
      const allData = [
        ...project.additionalInfo,
        ...types
          .filter(type => !existingTypes.includes(type))
          .map(type => {
            if (type === 'condominium') return { type, totalUnits: undefined, totalFloors: undefined };
            if (type === 'detached') return { type, totalUnits: undefined };
            if (type === 'semi-detached') return { type, totalUnits: undefined, totalHomes: undefined };
            return { type: 'townhouse' as const, totalBuildings: undefined, totalHomes: undefined };
          })
      ];
      // Sort to maintain consistent order
      const sortOrder = { 'condominium': 0, 'detached': 1, 'semi-detached': 2, 'townhouse': 3 };
      allData.sort((a, b) => sortOrder[a.type] - sortOrder[b.type]);
      setUnitInfoData(allData);
    }
    setEditingUnitInfo(true);
  };

  const handleCancelUnitInfo = () => {
    setEditingUnitInfo(false);
  };

  const handleSaveUnitInfo = async () => {
    if (!project) return;
    try {
      setSavingUnitInfo(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { additionalInfo: unitInfoData }
      );
      setProject(response.data.project);
      setEditingUnitInfo(false);
      toast.success('Unit information updated successfully');
    } catch (err: any) {
      console.error('Error updating unit info:', err);
      toast.error(err.response?.data?.message || 'Failed to update unit information');
    } finally {
      setSavingUnitInfo(false);
    }
  };

  // Spaces handlers
  const handleEditSpaces = () => {
    if (!project) return;
    setSpacesData(project.spaces || []);
    setEditingSpaces(true);
  };

  const handleCancelSpaces = () => {
    setEditingSpaces(false);
  };

  const handleSpacesChange = (newSpaces: string[]) => {
    setSpacesData(newSpaces);
  };

  const handleAddSpacesFromModal = (newSpaces: string[]) => {
    setSpacesData(prev => [...prev, ...newSpaces]);
  };

  const handleSaveSpaces = async () => {
    if (!project) return;
    try {
      setSavingSpaces(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { spaces: spacesData }
      );
      setProject(response.data.project);
      setEditingSpaces(false);
      toast.success('Spaces updated successfully');
    } catch (err: any) {
      console.error('Error updating spaces:', err);
      toast.error(err.response?.data?.message || 'Failed to update spaces');
    } finally {
      setSavingSpaces(false);
    }
  };

  // Levels handlers
  const handleEditLevels = () => {
    if (!project) return;
    setLevelsData(project.levels || []);
    setEditingLevels(true);
  };

  const handleCancelLevels = () => {
    setEditingLevels(false);
  };

  const handleLevelsChange = (newLevels: string[]) => {
    setLevelsData(newLevels);
  };

  const handleAddLevelsFromModal = (newLevels: string[]) => {
    setLevelsData(prev => [...prev, ...newLevels]);
  };

  const handleSaveLevels = async () => {
    if (!project) return;
    try {
      setSavingLevels(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { levels: levelsData }
      );
      setProject(response.data.project);
      setEditingLevels(false);
      toast.success('Levels updated successfully');
    } catch (err: any) {
      console.error('Error updating levels:', err);
      toast.error(err.response?.data?.message || 'Failed to update levels');
    } finally {
      setSavingLevels(false);
    }
  };

  // Views handlers
  const handleEditViews = () => {
    if (!project) return;
    setViewsData(project.views || []);
    setEditingViews(true);
  };

  const handleCancelViews = () => {
    setEditingViews(false);
  };

  const handleViewsChange = (newViews: string[]) => {
    setViewsData(newViews);
  };

  const handleAddViewsFromModal = (newViews: string[]) => {
    setViewsData(prev => [...prev, ...newViews]);
  };

  const handleSaveViews = async () => {
    if (!project) return;
    try {
      setSavingViews(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { views: viewsData }
      );
      setProject(response.data.project);
      setEditingViews(false);
      toast.success('Views updated successfully');
    } catch (err: any) {
      console.error('Error updating views:', err);
      toast.error(err.response?.data?.message || 'Failed to update views');
    } finally {
      setSavingViews(false);
    }
  };

  // Features handlers
  const handleEditFeatures = () => {
    if (!project) return;
    setFeaturesData(project.features || '');
    setEditingFeatures(true);
  };

  const handleCancelFeatures = () => {
    setEditingFeatures(false);
  };

  const handleSaveFeatures = async () => {
    if (!project) return;
    try {
      setSavingFeatures(true);
      const response = await api.put(
        `/workspaces/${workspaceId}/projects/${projectId}`,
        { features: featuresData || undefined }
      );
      setProject(response.data.project);
      setEditingFeatures(false);
      toast.success('Features updated successfully');
    } catch (err: any) {
      console.error('Error updating features:', err);
      toast.error(err.response?.data?.message || 'Failed to update features');
    } finally {
      setSavingFeatures(false);
    }
  };

  // Amenity handlers
  const handleCreateAmenity = () => {
    setEditingAmenity(null);
    setAmenityFormOpen(true);
  };

  const handleEditAmenity = (amenity: Amenity) => {
    setEditingAmenity(amenity);
    setAmenityFormOpen(true);
    setAmenityViewOpen(false);
  };

  const handleViewAmenity = (amenity: Amenity) => {
    setSelectedAmenity(amenity);
    setAmenityViewOpen(true);
  };

  const handleSaveAmenity = async (data: AmenityFormData) => {
    try {
      if (editingAmenity) {
        await updateAmenity(workspaceId!, projectId!, editingAmenity._id, data);
        toast.success('Amenity updated successfully');
      } else {
        await createAmenity(workspaceId!, projectId!, data);
        toast.success('Amenity created successfully');
      }
      setAmenityFormOpen(false);
      setEditingAmenity(null);
      fetchAmenities();
    } catch (err: any) {
      console.error('Error saving amenity:', err);
      toast.error(err.response?.data?.message || 'Failed to save amenity');
    }
  };

  const handleDeleteAmenity = async (amenity: Amenity) => {
    setConfirmDialogConfig({
      title: 'Delete Amenity',
      message: 'Are you sure you want to delete this amenity? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteAmenity(workspaceId!, projectId!, amenity._id);
          toast.success('Amenity deleted successfully');
          setConfirmDialogOpen(false);
          setAmenityViewOpen(false);
          fetchAmenities();
        } catch (err: any) {
          console.error('Error deleting amenity:', err);
          toast.error(err.response?.data?.message || 'Failed to delete amenity');
        }
      }
    });
    setConfirmDialogOpen(true);
  };

  const formatAddress = (addr?: ProjectAddress) => {
    if (!addr) return 'No address provided';
    const parts = [addr.street1];
    if (addr.street2) parts.push(addr.street2);
    parts.push(addr.city);
    parts.push(addr.state);
    parts.push(addr.zip);
    parts.push(addr.country);
    return parts.filter(p => p).join(', ');
  };

  if (loading) {
    return (
      <AppLayout onBack={handleBack}>
        <div className="project-details-loading">
          <div className="projects-loading-spinner" />
        </div>
      </AppLayout>
    );
  }

  if (error || !project) {
    return (
      <AppLayout onBack={handleBack}>
        <div className="project-details-error">
          <p>{error || 'Project not found'}</p>
          <button className="btn btn-primary btn-md" onClick={handleBack}>
            Back to Projects
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBack}>
      <div className="project-details-container">
        {/* Header Section with Background and Tabs */}
        <div className="project-details-header-section">
          <div className="project-details-header-top">
            <button className="project-details-back-btn" onClick={handleBack}>
              <ArrowLeft size={24} />
            </button>
            <div className="project-details-header-info">
              <h1>{project.name}</h1>
              <p>{formatAddress(project.projectAddress)}</p>
            </div>
            <button
              className="project-details-delete-btn"
              onClick={() => setShowDeleteDialog(true)}
              title="Delete project"
            >
              <Trash2 size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="project-details-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`project-details-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="project-details-tab-content">
          {/* GENERAL TAB */}
          {activeTab === 'general' && (
            <div className="project-details-general">
              {/* Three Logo Cards Row */}
              <div className="project-details-row project-details-logos-row">
                {/* Project Logo */}
                <div className="project-details-card project-details-logo-card">
                  <div className="project-details-logo-display">
                    {uploadingProjectLogo ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '180px' }}>
                        <div className="projects-loading-spinner" />
                      </div>
                    ) : project.logoId && workspaceId ? (
                      <S3Image
                        workspaceId={workspaceId}
                        imageId={project.logoId}
                        alt={project.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '180px',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <div className="project-details-logo-placeholder-box">
                        {project.name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <button
                    className="project-details-logo-edit-overlay"
                    onClick={handleProjectLogoClick}
                    disabled={uploadingProjectLogo}
                  >
                    <Pencil size={20} />
                  </button>
                  <input
                    ref={projectLogoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProjectLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <div className="project-details-logo-label">Project Logo</div>
                </div>

                {/* Purchaser Portal Logo */}
                <div className="project-details-card project-details-logo-card">
                  <div className="project-details-logo-display">
                    {uploadingPortalLogo ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '180px' }}>
                        <div className="projects-loading-spinner" />
                      </div>
                    ) : project.portalHomeLogoId && workspaceId ? (
                      <S3Image
                        workspaceId={workspaceId}
                        imageId={project.portalHomeLogoId}
                        alt="Purchaser Portal Logo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '180px',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <div className="project-details-logo-placeholder-box">
                        PP
                      </div>
                    )}
                  </div>
                  <button
                    className="project-details-logo-edit-overlay"
                    onClick={handlePortalLogoClick}
                    disabled={uploadingPortalLogo}
                  >
                    <Pencil size={20} />
                  </button>
                  <input
                    ref={portalLogoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePortalLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <div className="project-details-logo-label">Purchaser Portal Logo</div>
                </div>

                {/* Construction Company Logo */}
                <div className="project-details-card project-details-logo-card">
                  <div className="project-details-logo-display">
                    {uploadingConstructionLogo ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '180px' }}>
                        <div className="projects-loading-spinner" />
                      </div>
                    ) : project.constructionLogoId && workspaceId ? (
                      <S3Image
                        workspaceId={workspaceId}
                        imageId={project.constructionLogoId}
                        alt="Construction Company Logo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '180px',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <div className="project-details-logo-placeholder-box">
                        CC
                      </div>
                    )}
                  </div>
                  <button
                    className="project-details-logo-edit-overlay"
                    onClick={handleConstructionLogoClick}
                    disabled={uploadingConstructionLogo}
                  >
                    <Pencil size={20} />
                  </button>
                  <input
                    ref={constructionLogoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleConstructionLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <div className="project-details-logo-label">Construction Company Logo</div>
                </div>
              </div>

              {/* Row for Basic Info and Addresses */}
              <div className="project-details-row">
                {/* Basic Information */}
                <div className="project-details-card">
                  <div className="project-details-card-header">
                    <h2>Basic Information</h2>
                    {editingBasicInfo ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelBasicInfo}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveBasicInfo} disabled={savingBasicInfo}>
                          {savingBasicInfo ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditBasicInfo}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingBasicInfo ? (
                    <div className="project-details-edit-form">
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>PROJECT NAME:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.name}
                            onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group">
                          <label>PROJECT CODE:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.projectCode}
                            onChange={(e) => handleBasicInfoChange('projectCode', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>SELECTION PHASE:</label>
                          <select
                            className="form-input"
                            value={basicInfoData.ongoingSelectionPhase}
                            onChange={(e) => handleBasicInfoChange('ongoingSelectionPhase', e.target.value)}
                          >
                            <option value="">Select Phase</option>
                            <option value="One">One</option>
                            <option value="Two">Two</option>
                            <option value="Three">Three</option>
                            <option value="Four">Four</option>
                            <option value="Five">Five</option>
                          </select>
                        </div>
                        <div className="project-details-form-group">
                          <label>STATUS:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.status}
                            onChange={(e) => handleBasicInfoChange('status', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>CONTACT NAME:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.contactName}
                            onChange={(e) => handleBasicInfoChange('contactName', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group">
                          <label>CONTACT EMAIL:</label>
                          <input
                            type="email"
                            className="form-input"
                            value={basicInfoData.contactEmail}
                            onChange={(e) => handleBasicInfoChange('contactEmail', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>CONTACT PHONE:</label>
                          <input
                            type="tel"
                            className="form-input"
                            value={basicInfoData.contactPhone}
                            onChange={(e) => handleBasicInfoChange('contactPhone', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group">
                          <label>WEBSITE:</label>
                          <input
                            type="url"
                            className="form-input"
                            value={basicInfoData.contactWebsite}
                            onChange={(e) => handleBasicInfoChange('contactWebsite', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="project-details-form-row">
                        <div className="project-details-form-group">
                          <label>CONSTRUCTION START:</label>
                          <input
                            type="date"
                            className="form-input"
                            value={basicInfoData.constructionStartDate}
                            onChange={(e) => handleBasicInfoChange('constructionStartDate', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group">
                          <label>ESTIMATED COMPLETION:</label>
                          <input
                            type="date"
                            className="form-input"
                            value={basicInfoData.estimatedCompletionDate}
                            onChange={(e) => handleBasicInfoChange('estimatedCompletionDate', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="project-details-grid">
                      <div className="project-details-field">
                        <label>PROJECT NAME:</label>
                        <span>{project.name}</span>
                      </div>
                      <div className="project-details-field">
                        <label>PROJECT CODE:</label>
                        <span>{project.projectCode || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>BUILDER:</label>
                        <span>{project.builderName || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>SELECTION PHASE:</label>
                        <span>{project.ongoingSelectionPhase || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>STATUS:</label>
                        <span>{project.status || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>CONTACT NAME:</label>
                        <span>{project.contactName || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>CONTACT EMAIL:</label>
                        <span>{project.contactEmail || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>CONTACT PHONE:</label>
                        <span>{project.contactPhone || '—'}</span>
                      </div>
                      <div className="project-details-field">
                        <label>WEBSITE:</label>
                        <span>
                          {project.contactWebsite ? (
                            <a href={project.contactWebsite} target="_blank" rel="noopener noreferrer">
                              {project.contactWebsite}
                            </a>
                          ) : (
                            '—'
                          )}
                        </span>
                      </div>
                      <div className="project-details-field">
                        <label>CONSTRUCTION START:</label>
                        <span>
                          {project.constructionStartDate
                            ? new Date(project.constructionStartDate).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                      <div className="project-details-field">
                        <label>ESTIMATED COMPLETION:</label>
                        <span>
                          {project.estimatedCompletionDate
                            ? new Date(project.estimatedCompletionDate).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Combined Addresses Card */}
                <div className="project-details-card">
                  <div className="project-details-card-header">
                    <h2>Addresses</h2>
                  </div>

                  {/* Project Address Section */}
                  <div style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#48182F', margin: 0 }}>Project Address</h3>
                      {editingProjectAddress ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-secondary btn-sm" onClick={handleCancelProjectAddress}>
                            Cancel
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={handleSaveProjectAddress} disabled={savingProjectAddress}>
                            {savingProjectAddress ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      ) : (
                        <button className="project-details-edit-btn" onClick={handleEditProjectAddress}>
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>

                    {editingProjectAddress ? (
                      <div className="project-details-edit-form">
                        <div className="project-details-form-group-full">
                          <label>STREET ADDRESS 1:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={projectAddressData.street1}
                            onChange={(e) => handleProjectAddressChange('street1', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group-full">
                          <label>STREET ADDRESS 2:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={projectAddressData.street2}
                            onChange={(e) => handleProjectAddressChange('street2', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-row">
                          <div className="project-details-form-group">
                            <label>CITY:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={projectAddressData.city}
                              onChange={(e) => handleProjectAddressChange('city', e.target.value)}
                            />
                          </div>
                          <div className="project-details-form-group">
                            <label>STATE/PROVINCE:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={projectAddressData.state}
                              onChange={(e) => handleProjectAddressChange('state', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="project-details-form-row">
                          <div className="project-details-form-group">
                            <label>ZIP/POSTAL CODE:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={projectAddressData.zip}
                              onChange={(e) => handleProjectAddressChange('zip', e.target.value)}
                            />
                          </div>
                          <div className="project-details-form-group">
                            <label>COUNTRY:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={projectAddressData.country}
                              onChange={(e) => handleProjectAddressChange('country', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="project-details-grid">
                        <div className="project-details-field full-width">
                          <label>ADDRESS:</label>
                          <span>{formatAddress(project.projectAddress)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Office Address Section */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#48182F', margin: 0 }}>Office Address</h3>
                      {editingOfficeAddress ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-secondary btn-sm" onClick={handleCancelOfficeAddress}>
                            Cancel
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={handleSaveOfficeAddress} disabled={savingOfficeAddress}>
                            {savingOfficeAddress ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      ) : (
                        <button className="project-details-edit-btn" onClick={handleEditOfficeAddress}>
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>

                    {editingOfficeAddress ? (
                      <div className="project-details-edit-form">
                        <div className="project-details-form-group-full">
                          <label>STREET ADDRESS 1:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={officeAddressData.street1}
                            onChange={(e) => handleOfficeAddressChange('street1', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-group-full">
                          <label>STREET ADDRESS 2:</label>
                          <input
                            type="text"
                            className="form-input"
                            value={officeAddressData.street2}
                            onChange={(e) => handleOfficeAddressChange('street2', e.target.value)}
                          />
                        </div>
                        <div className="project-details-form-row">
                          <div className="project-details-form-group">
                            <label>CITY:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={officeAddressData.city}
                              onChange={(e) => handleOfficeAddressChange('city', e.target.value)}
                            />
                          </div>
                          <div className="project-details-form-group">
                            <label>STATE/PROVINCE:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={officeAddressData.state}
                              onChange={(e) => handleOfficeAddressChange('state', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="project-details-form-row">
                          <div className="project-details-form-group">
                            <label>ZIP/POSTAL CODE:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={officeAddressData.zip}
                              onChange={(e) => handleOfficeAddressChange('zip', e.target.value)}
                            />
                          </div>
                          <div className="project-details-form-group">
                            <label>COUNTRY:</label>
                            <input
                              type="text"
                              className="form-input"
                              value={officeAddressData.country}
                              onChange={(e) => handleOfficeAddressChange('country', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="project-details-grid">
                        <div className="project-details-field full-width">
                          <label>ADDRESS:</label>
                          <span>{formatAddress(project.officeAddress)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description - Full Width */}
              <div style={{ padding: '0 40px', marginBottom: '24px' }}>
                <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Description</h2>
                  {editingDescription ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelDescription}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveDescription} disabled={savingDescription}>
                        {savingDescription ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <button className="project-details-edit-btn" onClick={handleEditDescription}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {editingDescription ? (
                  <div className="project-details-edit-form">
                    <RichTextEditor
                      content={descriptionData}
                      onChange={setDescriptionData}
                      placeholder="Enter project description..."
                    />
                  </div>
                ) : (
                  <div className="project-details-description">
                    <div dangerouslySetInnerHTML={{ __html: project.description || '<p>No description provided.</p>' }} />
                  </div>
                )}
              </div>
              </div>

              {/* Row for Intro Video (1/3) and Unit/Lot Information (2/3) */}
              <div className="project-details-row project-details-row-video-unit">
                {/* Intro Video */}
                <div className="project-details-card project-details-card-video">
                  <div className="project-details-card-header">
                    <h2>Intro Video</h2>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {project.introVideoUrl && (
                        <button
                          className="project-details-edit-btn"
                          onClick={handleRemoveVideo}
                          disabled={uploadingVideo}
                          title="Remove video"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                      <button
                        className="project-details-edit-btn"
                        onClick={() => setShowVideoModal(true)}
                        disabled={uploadingVideo}
                      >
                        <Pencil size={16} />
                      </button>
                    </div>
                  </div>
                  {project.introVideoUrl && project.introVideoType ? (
                    <div className="project-details-video-container">
                      <VideoPlayer
                        videoUrl={project.introVideoUrl}
                        videoType={project.introVideoType}
                      />
                    </div>
                  ) : (
                    <div className="project-details-video-placeholder" onClick={() => setShowVideoModal(true)} style={{ cursor: 'pointer' }}>
                      <div className="project-video-icon">
                        <Camera size={48} style={{ color: '#9CA3AF' }} />
                      </div>
                      <p>NO VIDEO AVAILABLE</p>
                      <p style={{ fontSize: '12px', marginTop: '8px', color: '#6B7280' }}>Click to add video</p>
                    </div>
                  )}
                </div>

                {/* Unit/Lot Information */}
                <div className="project-details-card project-details-card-unit">
                  <div className="project-details-card-header">
                    <h2>Unit/Lot Information</h2>
                    {editingUnitInfo ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelUnitInfo}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveUnitInfo} disabled={savingUnitInfo}>
                          {savingUnitInfo ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditUnitInfo}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingUnitInfo ? (
                    <div className="project-details-edit-form">
                      {unitInfoData.map((info, index) => (
                        <div key={index} style={{ marginBottom: '20px' }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: '1px solid #E5E7EB'
                          }}>
                            {info.type === 'condominium' && 'Condominium'}
                            {info.type === 'detached' && 'Detached Homes'}
                            {info.type === 'semi-detached' && 'Semi-Detached Homes'}
                            {info.type === 'townhouse' && 'Townhouses'}
                          </div>
                          <div className="project-details-form-row">
                            {info.type === 'condominium' && (
                              <>
                                <div className="project-details-form-group">
                                  <label>TOTAL UNITS/LOTS:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalUnits || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalUnits: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                                <div className="project-details-form-group">
                                  <label>TOTAL FLOORS:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalFloors || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalFloors: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                              </>
                            )}
                            {info.type === 'detached' && (
                              <div className="project-details-form-group">
                                <label>TOTAL UNITS/LOTS:</label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={info.totalUnits || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setUnitInfoData(prev => {
                                      const updated = [...prev];
                                      updated[index] = { ...updated[index], totalUnits: value ? parseInt(value) : undefined };
                                      return updated;
                                    });
                                  }}
                                />
                              </div>
                            )}
                            {info.type === 'semi-detached' && (
                              <>
                                <div className="project-details-form-group">
                                  <label>TOTAL UNITS/LOTS:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalUnits || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalUnits: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                                <div className="project-details-form-group">
                                  <label>TOTAL HOMES:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalHomes || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalHomes: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                              </>
                            )}
                            {info.type === 'townhouse' && (
                              <>
                                <div className="project-details-form-group">
                                  <label>TOTAL BUILDINGS:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalBuildings || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalBuildings: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                                <div className="project-details-form-group">
                                  <label>TOTAL HOMES:</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={info.totalHomes || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setUnitInfoData(prev => {
                                        const updated = [...prev];
                                        updated[index] = { ...updated[index], totalHomes: value ? parseInt(value) : undefined };
                                        return updated;
                                      });
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      {project.additionalInfo && project.additionalInfo.length > 0 ? (
                        project.additionalInfo.map((info, index) => (
                          <div key={index} style={{ marginBottom: '16px' }}>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#374151',
                              marginBottom: '12px',
                              paddingBottom: '8px',
                              borderBottom: '1px solid #E5E7EB'
                            }}>
                              {info.type === 'condominium' && 'Condominium'}
                              {info.type === 'detached' && 'Detached Homes'}
                              {info.type === 'semi-detached' && 'Semi-Detached Homes'}
                              {info.type === 'townhouse' && 'Townhouses'}
                            </div>
                            <div className="project-details-grid">
                              {info.type === 'condominium' && (
                                <>
                                  <div className="project-details-field">
                                    <label>TOTAL UNITS/LOTS:</label>
                                    <span>{info.totalUnits || '—'}</span>
                                  </div>
                                  <div className="project-details-field">
                                    <label>TOTAL FLOORS:</label>
                                    <span>{info.totalFloors || '—'}</span>
                                  </div>
                                </>
                              )}
                              {info.type === 'detached' && (
                                <div className="project-details-field">
                                  <label>TOTAL UNITS/LOTS:</label>
                                  <span>{info.totalUnits || '—'}</span>
                                </div>
                              )}
                              {info.type === 'semi-detached' && (
                                <>
                                  <div className="project-details-field">
                                    <label>TOTAL UNITS/LOTS:</label>
                                    <span>{info.totalUnits || '—'}</span>
                                  </div>
                                  <div className="project-details-field">
                                    <label>TOTAL HOMES:</label>
                                    <span>{info.totalHomes || '—'}</span>
                                  </div>
                                </>
                              )}
                              {info.type === 'townhouse' && (
                                <>
                                  <div className="project-details-field">
                                    <label>TOTAL BUILDINGS:</label>
                                    <span>{info.totalBuildings || '—'}</span>
                                  </div>
                                  <div className="project-details-field">
                                    <label>TOTAL HOMES:</label>
                                    <span>{info.totalHomes || '—'}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No unit/lot information provided</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Row for Spaces, Levels, and Views with max height and scroll */}
              <div className="project-details-row project-details-row-scrollable">
                {/* Spaces */}
                <div className="project-details-card project-details-card-scrollable">
                  <div className="project-details-card-header">
                    <h2>Spaces</h2>
                    {editingSpaces ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelSpaces}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveSpaces} disabled={savingSpaces}>
                          {savingSpaces ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditSpaces}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingSpaces ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddSpacesModal(true)}
                        style={{ marginBottom: '12px' }}
                      >
                        + Add Spaces
                      </button>
                      <SortableList
                        items={spacesData}
                        onChange={handleSpacesChange}
                        placeholder="Add custom space name"
                        addButtonText="+"
                      />
                      <AddItemsModal
                        isOpen={showAddSpacesModal}
                        onClose={() => setShowAddSpacesModal(false)}
                        onAdd={handleAddSpacesFromModal}
                        availableOptions={[
                          '2nd Bedroom',
                          '3rd Bedroom',
                          '4th Bedroom',
                          'Balcony',
                          'Den',
                          'Dining Room',
                          'Ensuite Bathroom',
                          'Family Room',
                          'Foyer',
                          'Great Room',
                          'Kitchen',
                          'Laundry Room',
                          'Living Room',
                          'Loft',
                          'Main Bathroom',
                          'Master Bedroom',
                          'Powder Room',
                          'Primary Bedroom',
                          'Sitting Area',
                          'Solarium',
                          'Storage',
                          'Study',
                          'Terrace',
                          'Walk-in Closet'
                        ]}
                        title="Add Spaces"
                      />
                    </>
                  ) : (
                    <div className="project-details-list">
                      {project.spaces && project.spaces.length > 0 ? (
                        project.spaces.map((space, index) => (
                          <div key={index} className="project-details-list-item">
                            {space}
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No spaces defined</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Levels */}
                <div className="project-details-card project-details-card-scrollable">
                  <div className="project-details-card-header">
                    <h2>Levels</h2>
                    {editingLevels ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelLevels}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveLevels} disabled={savingLevels}>
                          {savingLevels ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditLevels}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingLevels ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddLevelsModal(true)}
                        style={{ marginBottom: '12px' }}
                      >
                        + Add Levels
                      </button>
                      <SortableList
                        items={levelsData}
                        onChange={handleLevelsChange}
                        placeholder="Add custom level name"
                        addButtonText="+"
                      />
                      <AddItemsModal
                        isOpen={showAddLevelsModal}
                        onClose={() => setShowAddLevelsModal(false)}
                        onAdd={handleAddLevelsFromModal}
                        availableOptions={[
                          'Ground Floor',
                          'Basement',
                          'Level 1',
                          'Level 2',
                          'Level 3',
                          'Level 4',
                          'Level 5',
                          'Level 6',
                          'Level 7',
                          'Level 8',
                          'Level 9',
                          'Level 10',
                          'Mezzanine',
                          'Penthouse',
                          'Rooftop',
                          'Parking Level P1',
                          'Parking Level P2',
                          'Parking Level P3',
                          'Parking Level P4',
                          'Parking Level P5'
                        ]}
                        title="Add Levels"
                      />
                    </>
                  ) : (
                    <div className="project-details-list">
                      {project.levels && project.levels.length > 0 ? (
                        project.levels.map((level, index) => (
                          <div key={index} className="project-details-list-item">
                            {level}
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No levels defined</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Views */}
                <div className="project-details-card project-details-card-scrollable">
                  <div className="project-details-card-header">
                    <h2>Views</h2>
                    {editingViews ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelViews}>
                          Cancel
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={handleSaveViews} disabled={savingViews}>
                          {savingViews ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button className="project-details-edit-btn" onClick={handleEditViews}>
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>

                  {editingViews ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddViewsModal(true)}
                        style={{ marginBottom: '12px' }}
                      >
                        + Add Views
                      </button>
                      <SortableList
                        items={viewsData}
                        onChange={handleViewsChange}
                        placeholder="Add custom view name"
                        addButtonText="+"
                      />
                      <AddItemsModal
                        isOpen={showAddViewsModal}
                        onClose={() => setShowAddViewsModal(false)}
                        onAdd={handleAddViewsFromModal}
                        availableOptions={[
                          'City View',
                          'Garden View',
                          'Lake View',
                          'Mountain View',
                          'Ocean View',
                          'Park View',
                          'Pool View',
                          'River View',
                          'Skyline View',
                          'Street View',
                          'Unobstructed View',
                          'Valley View',
                          'Water View'
                        ]}
                        title="Add Views"
                      />
                    </>
                  ) : (
                    <div className="project-details-list">
                      {project.views && project.views.length > 0 ? (
                        project.views.map((view, index) => (
                          <div key={index} className="project-details-list-item">
                            {view}
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No views defined</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* FEATURES TAB */}
          {activeTab === 'features' && (
            <div className="project-details-features">
              {/* Features Description */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Features and Finishes</h2>
                  {editingFeatures ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelFeatures}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveFeatures} disabled={savingFeatures}>
                        {savingFeatures ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <button className="project-details-edit-btn" onClick={handleEditFeatures}>
                      <Pencil size={16} />
                    </button>
                  )}
                </div>

                {editingFeatures ? (
                  <div className="project-details-edit-form">
                    <RichTextEditor
                      content={featuresData}
                      onChange={setFeaturesData}
                      placeholder="Describe features and finishes..."
                    />
                  </div>
                ) : (
                  <div className="project-details-description">
                    <div dangerouslySetInnerHTML={{ __html: project.features || '<p>No features and finishes information provided.</p>' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AMENITIES TAB */}
          {activeTab === 'amenities' && (
            <div className="project-details-amenities">
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Amenities</h2>
                  <button className="btn btn-primary btn-sm" onClick={handleCreateAmenity}>
                    + Add Amenity
                  </button>
                </div>

                {loadingAmenities ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <div className="projects-loading-spinner" />
                  </div>
                ) : amenities.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No amenities added yet</p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '24px',
                    marginTop: '20px'
                  }}>
                    {amenities.map((amenity) => (
                      <AmenityCard
                        key={amenity._id}
                        amenity={amenity}
                        workspaceId={workspaceId!}
                        onClick={() => handleViewAmenity(amenity)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DOCUMENTS TAB */}
          {activeTab === 'documents' && (
            <div className="project-details-documents">
              {/* Legal Documents */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Legal Documents</h2>
                </div>
                <DocumentGallery
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  fileType="legal_document"
                  acceptTypes=".doc,.docx"
                  helpText="All legal agreements and amendments can be uploaded here. Only Word Templates are accepted."
                />
              </div>

              {/* Marketing Documents */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Marketing Documents</h2>
                </div>
                <DocumentGallery
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  fileType="marketing_document"
                  acceptTypes=".pdf,.doc,.docx"
                  helpText="Please upload PDF or Word Templates"
                />
              </div>

              {/* Amendments to Track */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Amendments to Track</h2>
                </div>
                <DocumentGallery
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  fileType="amendments_track"
                  acceptTypes=".pdf,.doc,.docx"
                  helpText="Amendments can be used to track signed amendments for each deals."
                />
              </div>
            </div>
          )}

          {/* MEDIA TAB */}
          {activeTab === 'media' && (
            <div className="project-details-media">
              {/* Photo Gallery */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Photo Gallery</h2>
                </div>
                <PhotoGallery
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  imageType="photo"
                  allowMultiple={true}
                />
              </div>

              {/* Video Gallery */}
              <div className="project-details-card">
                <div className="project-details-card-header">
                  <h2>Video Gallery</h2>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => videoGalleryRef.current?.openAddDialog()}
                  >
                    <LinkIcon size={16} style={{ marginRight: '6px' }} />
                    Add Video
                  </button>
                </div>
                <VideoGallery
                  ref={videoGalleryRef}
                  workspaceId={workspaceId!}
                  entityType="project"
                  entityId={projectId!}
                  videos={videos}
                  onAddVideo={handleAddVideo}
                  onDeleteVideo={handleDeleteVideo}
                  onUpdateVideo={handleUpdateVideo}
                  hideHeader={true}
                />
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Project"
        message={`Are you sure you want to delete "${project.name}"? This action cannot be undone and will remove all associated data including photos and videos.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteProject}
        onClose={() => setShowDeleteDialog(false)}
        loading={deleting}
      />

      {/* Generic Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        title={confirmDialogConfig.title}
        message={confirmDialogConfig.message}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={confirmDialogConfig.onConfirm}
        onClose={() => setConfirmDialogOpen(false)}
      />


      {/* Amenity Form Modal */}
      <AmenityFormModal
        isOpen={amenityFormOpen}
        onClose={() => {
          setAmenityFormOpen(false);
          setEditingAmenity(null);
        }}
        onSave={handleSaveAmenity}
        amenity={editingAmenity}
        workspaceId={workspaceId!}
        projectId={projectId!}
      />

      {/* Amenity View Modal */}
      <AmenityViewModal
        isOpen={amenityViewOpen}
        onClose={() => {
          setAmenityViewOpen(false);
          setSelectedAmenity(null);
        }}
        amenity={selectedAmenity}
        workspaceId={workspaceId!}
        onEdit={handleEditAmenity}
        onDelete={handleDeleteAmenity}
      />

      {/* Video Upload Modal */}
      <VideoUploadModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        onVideoUploaded={handleVideoUploaded}
        workspaceId={workspaceId!}
        projectId={projectId!}
        currentVideoUrl={project?.introVideoUrl}
      />
    </AppLayout>
  );
};

export default ProjectDetails;
